import React from 'react';
import courtImg from './netball-ground.jpg';

const courtStyle = {
  wrapper: {
    textAlign: 'center',
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px 0',
  },
  divisionName: {
    color: '#090C82',
    fontSize: '1rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '2px',
    marginBottom: '4px',
  },
  gameTitle: {
    color: '#090C82',
    fontSize: '1.5rem',
    fontWeight: 700,
    marginBottom: '4px',
  },
  gameDescription: {
    fontSize: '0.95rem',
    color: '#333',
    marginBottom: '24px',
  },
  courtContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: '650px',
    margin: '0 auto 30px',
  },
  courtImage: {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
  playerName: {
    position: 'absolute',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 600,
    textAlign: 'center',
    textShadow: '0 1px 3px rgba(0,0,0,0.7)',
    whiteSpace: 'nowrap',
    transform: 'translate(-50%, -50%)',
    background: 'rgba(0,0,0,0.45)',
    padding: '3px 8px',
    borderRadius: '3px',
  },
  sectionLine: {
    fontSize: '0.9rem',
    marginBottom: '6px',
    color: '#333',
  },
  sectionLabel: {
    fontWeight: 700,
  },
  sectionNames: {
    color: '#090C82',
  },
};

/*
 * Netball court positions mapped to percentage coordinates on the horizontal court.
 * Order matches NETBALL_POSITIONS in fieldGrid.js:
 *   Row 1: Wing Defence, Goal Attack
 *   Row 2: Goal Keeper, Centre, Goal Shooter
 *   Row 3: Goal Defence, Wing Attack
 */
const POSITION_COORDS = [
  // Row 1: Wing Defence (left-third top), Goal Attack (right-third top)
  { top: '25%', left: '28%' },
  { top: '25%', left: '72%' },
  // Row 2: Goal Keeper (left), Centre (middle), Goal Shooter (right)
  { top: '50%', left: '12%' },
  { top: '50%', left: '50%' },
  { top: '50%', left: '88%' },
  // Row 3: Goal Defence (left-third bottom), Wing Attack (right-third bottom)
  { top: '75%', left: '28%' },
  { top: '75%', left: '72%' },
];

function formatPlayerLabel(player) {
  if (!player || !player.name) return '';
  let label = player.name;
  if (player.number) label += ' #' + player.number;
  if (player.isCaptain) label += ' (c)';
  return label;
}

function formatListSection(players) {
  if (!players || players.length === 0) return '';
  return players
    .filter((p) => p.name)
    .map((p) => formatPlayerLabel(p))
    .join(',  ');
}

function formatOutSection(players) {
  if (!players || players.length === 0) return '';
  return players
    .filter((p) => p.name)
    .map((p) => {
      let label = p.name;
      if (p.number) label += ' #' + p.number;
      if (p.reason) label += ' (' + p.reason + ')';
      if (p.isCaptain) label += ' (c)';
      return label;
    })
    .join(',  ');
}

export function NetballCourt({ divisionName, gameTitle, gameDescription, selectionData }) {
  const field = selectionData.field || [];
  const interchange = selectionData.interchange || [];
  const playerIn = selectionData.in || [];
  const playerOut = selectionData.out || [];

  return (
    <div style={courtStyle.wrapper}>
      {divisionName && (
        <div style={courtStyle.divisionName}>{divisionName} Team Selections</div>
      )}
      {gameTitle && <div style={courtStyle.gameTitle}>{gameTitle}</div>}
      {gameDescription && <div style={courtStyle.gameDescription}>{gameDescription}</div>}

      <div style={courtStyle.courtContainer}>
        <img src={courtImg} alt="Netball court" style={courtStyle.courtImage} />
        {field.map((player, idx) => {
          const label = formatPlayerLabel(player);
          if (!label || idx >= POSITION_COORDS.length) return null;
          const coords = POSITION_COORDS[idx];
          return (
            <div
              key={idx}
              style={{ ...courtStyle.playerName, top: coords.top, left: coords.left }}
            >
              {label}
            </div>
          );
        })}
      </div>

      {interchange.length > 0 && (
        <div style={courtStyle.sectionLine}>
          <span style={courtStyle.sectionLabel}>Substitute - </span>
          <span style={courtStyle.sectionNames}>{formatListSection(interchange)}</span>
        </div>
      )}
      {playerIn.length > 0 && (
        <div style={courtStyle.sectionLine}>
          <span style={courtStyle.sectionLabel}>In - </span>
          <span style={courtStyle.sectionNames}>{formatListSection(playerIn)}</span>
        </div>
      )}
      {playerOut.length > 0 && (
        <div style={courtStyle.sectionLine}>
          <span style={courtStyle.sectionLabel}>Out - </span>
          <span style={courtStyle.sectionNames}>{formatOutSection(playerOut)}</span>
        </div>
      )}
    </div>
  );
}
